import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { providerService } from '../../services/providerService';

// Esquema de validación para el formulario de contacto
const contactSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es obligatorio'),
  email: Yup.string().email('Email inválido'),
  phone: Yup.string().matches(/^\d{9}$/, 'El teléfono debe tener 9 dígitos')
}).test('email-or-phone', 'Debe proporcionar al menos email o teléfono', function(values) {
  const { email, phone } = values || {};
  return (email && email.length > 0) || (phone && phone.length > 0);
});

function ContactsModal({ open, onClose, provider, onSave }) {
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Cargar contactos cuando se abre el modal
  useEffect(() => {
    if (open && provider) {
      setContacts(provider.contacts || []);
    }
  }, [open, provider]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: ''
    },
    validationSchema: contactSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      
      console.log('=== DEBUG ContactsModal onSubmit ===');
      console.log('Values received:', values);
      console.log('Provider ID:', provider?.id);
      console.log('Editing contact:', editingContact);
      
      try {
        if (editingContact) {
          // Para editar, necesitaríamos un endpoint específico
          // Por ahora, añadimos como nuevo y eliminamos el viejo
          console.log('Editing mode: deleting old contact');
          await handleDelete(editingContact.id, false);
          console.log('Adding new contact with values:', values);
          const result = await providerService.addContact(provider.id, values);
          console.log('Add contact result:', result);
          
          if (result.success) {
            const updatedProvider = await providerService.getProvider(provider.id);
            if (updatedProvider.success) {
              setContacts(updatedProvider.data.contacts || []);
              showSnackbar('Contacto actualizado correctamente');
            }
          } else {
            console.error('Error in addContact:', result);
            showSnackbar('Error al actualizar contacto: ' + result.message, 'error');
          }
        } else {
          // Añadir nuevo contacto
          console.log('Adding new contact with values:', values);
          console.log('Calling providerService.addContact with:', provider.id, values);
          const result = await providerService.addContact(provider.id, values);
          console.log('Add contact result:', result);
          
          if (result.success) {
            const updatedProvider = await providerService.getProvider(provider.id);
            if (updatedProvider.success) {
              setContacts(updatedProvider.data.contacts || []);
              showSnackbar('Contacto añadido correctamente');
            }
          } else {
            console.error('Error in addContact:', result);
            showSnackbar('Error al añadir contacto: ' + result.message, 'error');
          }
        }
        
        formik.resetForm();
        setEditingContact(null);
      } catch (error) {
        console.error('Exception in onSubmit:', error);
        showSnackbar('Error al procesar contacto', 'error');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleEdit = (contact) => {
    setEditingContact(contact);
    formik.setValues(contact);
  };

  const handleDelete = async (contactId, showMessage = true) => {
    if (showMessage && !window.confirm('¿Estás seguro de que deseas eliminar este contacto?')) {
      return;
    }

    try {
      const result = await providerService.deleteContact(contactId);
      
      if (result.success) {
        const updatedProvider = await providerService.getProvider(provider.id);
        if (updatedProvider.success) {
          setContacts(updatedProvider.data.contacts || []);
          if (showMessage) {
            showSnackbar('Contacto eliminado correctamente');
          }
        }
      } else {
        showSnackbar('Error al eliminar contacto: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al eliminar contacto', 'error');
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setEditingContact(null);
    onSave(contacts); // Notificar cambios al componente padre
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Contactos de {provider?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Formulario de contacto */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                {editingContact ? 'Editar Contacto' : 'Añadir Nuevo Contacto'}
              </Typography>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      name="name"
                      label="Nombre"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      name="email"
                      label="Email (opcional si hay teléfono)"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      name="phone"
                      label="Teléfono (opcional si hay email)"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      helperText={formik.touched.phone && formik.errors.phone}
                      disabled={submitting}
                      placeholder="611111111"
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ height: '56px' }}
                      disabled={submitting}
                    >
                      {submitting ? <CircularProgress size={20} /> : <AddIcon />}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Lista de contactos */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Contactos ({contacts.length})
              </Typography>
              <List>
                {contacts.map((contact, index) => (
                  <React.Fragment key={contact.id}>
                    <ListItem>
                      <ListItemText
                        primary={contact.name}
                        secondary={
                          <>
                            {contact.email && <div>{contact.email}</div>}
                            {contact.phone && <div>{contact.phone}</div>}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="editar"
                          onClick={() => handleEdit(contact)}
                          sx={{ mr: 1 }}
                          disabled={submitting}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="eliminar"
                          onClick={() => handleDelete(contact.id)}
                          disabled={submitting}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < contacts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ContactsModal; 